import { Loader2, Package, Users } from 'lucide-react';
import * as React from 'react';

import { useMobile } from '../../hooks/use-mobile';
import { cn } from '../../lib/utils';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';

/**
 * SharedPackageConflictDialog — STORY-1206 Q4 3-opciós shared-package
 * conflict dialog (B-10b, Javo gate-7 javaslat-vektor 2026-04-24).
 *
 * Trigger: Save → ha sharedEquipmentCount >= 2, dialog megjelenik.
 *
 * 3 opció:
 *  - "Mindegyikre" → master Package update direct (minden linkelt equipment látja).
 *    Audit: meglévő updatedBy + updatedAt (PACKAGE_MASTER_UPDATED event follow-up).
 *  - "Csak erre" → PackageCopyOnWriteService.copyForEquipment() invocation.
 *    Új variant package + Equipment.packageId re-link, master intakt.
 *  - "Mégse" → dialog close, save abort.
 *
 * UX:
 *  - Banner figyelmeztetés: "Ez a csomag {N} géppel megosztva. Változás mindre hat."
 *  - Mobile responsive: Dialog (desktop) + Sheet (mobile) via useMobile()
 *  - forceVariant escape hatch tesztekhez
 *  - Async confirm (loading state minden 3 button-ra) — parent kezeli a service hívást
 *  - Idempotent: ha sharedEquipmentCount < 2, dialog NEM nyílik meg (parent
 *    inline save flow)
 */

export type PackageEditorVariant = 'desktop' | 'mobile';

export type SharedPackageResolutionChoice = 'apply-to-all' | 'copy-for-this' | 'cancel';

export interface SharedPackageConflictDialogLabels {
  title: string;
  /** Banner: `${count} géppel megosztva` — placeholder substitution. */
  bannerText: (count: number) => string;
  bannerSubtext: string;
  applyToAllLabel: string;
  applyToAllDesc: string;
  copyForThisLabel: string;
  copyForThisDesc: string;
  cancelLabel: string;
  loadingApplyToAll: string;
  loadingCopyForThis: string;
  /** Gép-picker placeholder (copyTargets módban). */
  copyTargetPlaceholder: string;
  /** Gép-picker aria-label (copyTargets módban). */
  copyTargetAriaLabel: string;
}

const DEFAULT_LABELS: SharedPackageConflictDialogLabels = {
  title: 'Csomag megosztva',
  bannerText: (count: number) => `Ez a csomag ${count} géppel megosztva.`,
  // AC #6 verbatim text (sprint-11-14-story-breakdown §STORY-1206).
  bannerSubtext: 'Változás minden gépre hat. Hogyan szeretnéd kezelni?',
  applyToAllLabel: 'Mindegyikre',
  applyToAllDesc: 'A master csomag módosul. Minden linkelt gép az új verziót látja.',
  copyForThisLabel: 'Csak erre',
  // ⚠️ MVP warning (Sprint 13+ follow-up: inline diff-apply az új variantra).
  copyForThisDesc:
    'Új variant csomag jön létre erre a gépre. A többi gép változatlan master-t használja. Figyelem: jelenlegi szerkesztési módosításaid NEM kerülnek át az új variantra automatikusan — a variantra navigálás után újra szerkesztened kell.',
  cancelLabel: 'Mégse',
  loadingApplyToAll: 'Mentés...',
  loadingCopyForThis: 'Variant létrehozása...',
  copyTargetPlaceholder: 'Válassz gépet...',
  copyTargetAriaLabel: 'Cél-gép a fork-hoz',
};

/** Egy fork-célpont a gép-pickerben (EQUIPMENT-módban linkelt gépmodell). */
export interface SharedPackageCopyTarget {
  id: string;
  label: string;
}

export interface SharedPackageConflictDialogProps {
  open: boolean;
  /** Number of equipments sharing this master package. Dialog stays closed if <2. */
  sharedEquipmentCount: number;
  /** Equipment label for the "Csak erre" CTA (e.g., "SN-1234" or "Bérgép #42"). */
  currentEquipmentLabel?: string;
  /** Resolution callback. Returned promise → loading state in dialog. */
  onResolve: (choice: SharedPackageResolutionChoice) => Promise<void> | void;
  /** Triggered on backdrop click / Esc / "Mégse". */
  onOpenChange: (open: boolean) => void;
  labels?: Partial<SharedPackageConflictDialogLabels>;
  forceVariant?: PackageEditorVariant;
  /**
   * STORY-1206 #14 — fork-seam gating: amíg a copy-on-write BE-endpoint nem él,
   * a "Csak erre" opció elrejthető (kattintható zsákutca helyett).
   */
  hideCopyForThis?: boolean;
  /**
   * STORY-1206 #14 stage-2 (Javo-ratify 2026-06-11) — gép-picker a "Csak erre"
   * opcióhoz: a fork cél-gépmodelljei (linkedProducts). Ha megadott, a copy CTA
   * a kiválasztásig disabled. Controlled: a parent tartja a selectedCopyTargetId-t.
   */
  copyTargets?: SharedPackageCopyTarget[];
  selectedCopyTargetId?: string | null;
  onCopyTargetChange?: (id: string) => void;
}

export function SharedPackageConflictDialog({
  open,
  sharedEquipmentCount,
  currentEquipmentLabel,
  onResolve,
  onOpenChange,
  labels: labelOverrides,
  forceVariant,
  hideCopyForThis = false,
  copyTargets,
  selectedCopyTargetId,
  onCopyTargetChange,
}: SharedPackageConflictDialogProps) {
  const mobileAuto = useMobile();
  const variant: PackageEditorVariant = forceVariant ?? (mobileAuto ? 'mobile' : 'desktop');
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };

  const [pendingChoice, setPendingChoice] = React.useState<SharedPackageResolutionChoice | null>(
    null
  );
  // useRef in-flight flag — synchronous double-click guard (state guard
  // insufficient under React 18 batching).
  const inFlightRef = React.useRef(false);
  // Mount tracker — prevents state updates on unmounted component if the
  // parent drops sharedEquipmentCount mid-flight.
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Idempotent guard: dialog NEM nyílik meg sharedEquipmentCount < 2 esetén
  // VAGY ha NaN/negatív/non-finite. DEV warning ha a parent inkonzisztens
  // open=true + count<2 állapotot ad (misconfiguration jelzés).
  const validCount = Number.isFinite(sharedEquipmentCount) && Math.floor(sharedEquipmentCount) >= 2;
  const shouldRender = open && validCount;

  if (process.env.NODE_ENV !== 'production' && open && !validCount) {
    // eslint-disable-next-line no-console
    console.warn(
      `[SharedPackageConflictDialog] open=true but sharedEquipmentCount=${sharedEquipmentCount} ` +
        `(must be a finite integer >= 2). Dialog will silently stay closed — likely caller bug.`
    );
  }

  // Trim equipment label (whitespace-only would render "Csak erre (   )").
  const trimmedEquipmentLabel = currentEquipmentLabel?.trim() ?? '';

  const handleChoice = React.useCallback(
    async (choice: SharedPackageResolutionChoice) => {
      // Cancel is synchronous-fast: skip in-flight guard so user can always exit.
      if (choice === 'cancel') {
        try {
          await onResolve('cancel');
        } catch {
          // Swallow — cancel never fails meaningfully.
        }
        return;
      }
      if (inFlightRef.current) return; // synchronous double-click guard
      inFlightRef.current = true;
      setPendingChoice(choice);
      try {
        await onResolve(choice);
      } catch (err) {
        // Surface non-Error rejections (string/undefined) as console warnings;
        // parent felelőssége az error UI (toast etc.). Re-throw so the calling
        // promise chain sees it.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[SharedPackageConflictDialog] onResolve rejected:', err);
        }
        // Don't re-throw — keep loading state clear and let parent handle.
      } finally {
        inFlightRef.current = false;
        if (isMountedRef.current) {
          setPendingChoice(null);
        }
      }
    },
    [onResolve]
  );

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      // Esc / backdrop close: allow ALWAYS (cancel-equivalent semantics).
      // Pending non-cancel choice: short-circuit to cancel resolve.
      if (inFlightRef.current && pendingChoice !== 'cancel') {
        // Mid-flight non-cancel: do not close dialog (avoids tearing UI mid-tx).
        return;
      }
      onOpenChange(next);
    },
    [onOpenChange, pendingChoice]
  );

  const isLoading = pendingChoice !== null;

  // Shared body content (DRY across Dialog + Sheet variants)
  const bodyContent = (
    <>
      <Alert className="my-3 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <Users className="h-4 w-4 text-amber-700 dark:text-amber-300" aria-hidden="true" />
        <AlertDescription>
          <span className="font-medium text-amber-900 dark:text-amber-100">
            {labels.bannerText(sharedEquipmentCount)}
          </span>
          <span className="mt-1 block text-amber-800/80 dark:text-amber-200/80">
            {labels.bannerSubtext}
          </span>
        </AlertDescription>
      </Alert>

      <div className="space-y-2 py-2">
        <ChoiceCard
          icon={<Package className="h-4 w-4" aria-hidden="true" />}
          label={labels.applyToAllLabel}
          description={labels.applyToAllDesc}
          loading={pendingChoice === 'apply-to-all'}
          loadingLabel={labels.loadingApplyToAll}
          disabled={isLoading}
          onClick={() => void handleChoice('apply-to-all')}
          testId="conflict-dialog-apply-all"
          variant="default"
        />
        {!hideCopyForThis && (
          <>
            {copyTargets && copyTargets.length > 0 && (
              <select
                data-testid="conflict-dialog-copy-target"
                aria-label={labels.copyTargetAriaLabel}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedCopyTargetId ?? ''}
                disabled={isLoading}
                onChange={e => onCopyTargetChange?.(e.target.value)}
              >
                <option value="" disabled>
                  {labels.copyTargetPlaceholder}
                </option>
                {copyTargets.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
            <ChoiceCard
              icon={<Package className="h-4 w-4" aria-hidden="true" />}
              label={
                trimmedEquipmentLabel
                  ? `${labels.copyForThisLabel} (${trimmedEquipmentLabel})`
                  : labels.copyForThisLabel
              }
              description={labels.copyForThisDesc}
              loading={pendingChoice === 'copy-for-this'}
              loadingLabel={labels.loadingCopyForThis}
              disabled={isLoading || (!!copyTargets?.length && !selectedCopyTargetId)}
              onClick={() => void handleChoice('copy-for-this')}
              testId="conflict-dialog-copy"
              variant="outline"
            />
          </>
        )}
      </div>
    </>
  );

  const footerContent = (
    <Button
      type="button"
      variant="ghost"
      disabled={isLoading}
      onClick={() => void handleChoice('cancel')}
      data-testid="conflict-dialog-cancel"
    >
      {labels.cancelLabel}
    </Button>
  );

  if (variant === 'mobile') {
    return (
      <Sheet open={shouldRender} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[90vh] overflow-y-auto"
          data-testid="conflict-dialog-mobile"
        >
          <SheetHeader>
            <SheetTitle>{labels.title}</SheetTitle>
            <SheetDescription className="sr-only">{labels.bannerSubtext}</SheetDescription>
          </SheetHeader>
          {bodyContent}
          <SheetFooter>{footerContent}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={shouldRender} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="conflict-dialog-desktop">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription className="sr-only">{labels.bannerSubtext}</DialogDescription>
        </DialogHeader>
        {bodyContent}
        <DialogFooter>{footerContent}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Choice card sub-component ───────────────────────────────────────────────

interface ChoiceCardProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  loading: boolean;
  loadingLabel: string;
  disabled: boolean;
  onClick: () => void;
  testId: string;
  variant: 'default' | 'outline';
}

function ChoiceCard({
  icon,
  label,
  description,
  loading,
  loadingLabel,
  disabled,
  onClick,
  testId,
  variant,
}: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'group flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variant === 'default'
          ? 'border-primary bg-primary/5 hover:bg-primary/10'
          : 'border-border bg-background hover:bg-muted/50'
      )}
    >
      <div className="mt-0.5 shrink-0 text-foreground">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-label="loading" /> : icon}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="truncate font-medium text-foreground"
          title={loading ? loadingLabel : label}
        >
          {loading ? loadingLabel : label}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
      </div>
    </button>
  );
}
